import { useClubLogo } from '../lib/useClubLogo';
import React from 'react';
import { motion } from 'framer-motion';
import { Calendar, Users, Award, Heart, ChevronRight, Target, Shield, Star } from 'lucide-react';



const timeline = [
  {
    year: '1997',
    title: 'Vereinsgründung',
    text: 'Am 17. März 1997 wird der EHV Dresden Devils e.V. gegründet – entstanden aus einer engagierten Mannschaft der damaligen 1c-Eishockey-Abteilung des ESC Dresden. Der Verein wurde aus dem Wunsch heraus gegründet, Eishockey in Dresden selbstbestimmt, mit Herz und Gemeinschaftsgeist zu gestalten.'
  },
  {
    year: '2000er',
    title: 'Regionale Präsenz',
    text: 'Die Devils etablieren sich als feste Größe im regionalen Eishockey – mit Teilnahme an der Landesliga, Sachsenliga und anderen regionalen Ligen. Der Verein wächst und die Gemeinschaft wird stärker.'
  },
  {
    year: 'Heute',
    title: 'Tradition & Gemeinschaft',
    text: 'Ein bodenständiger Eishockey-Club, der mehr ist als nur ein Team. Ein Verein, der Tradition, Sport und Gemeinschaft verbindet und Raum für Wettkampf, persönliche Entwicklung und Freizeitaktivität im Eishockey-Sport bietet.'
  },
];

const philosophie = [
  {
    icon: Heart,
    title: 'Ehrliche Begeisterung',
    text: 'Eishockey-Begeisterung auf Amateur-Niveau – authentisch und mit vollem Herzen.',
    color: 'text-[#CC0000]',
  },
  {
    icon: Users,
    title: 'Miteinander',
    text: 'Das Miteinander steht im Mittelpunkt – sowohl auf als auch abseits des Eises.',
    color: 'text-[#46C7FF]',
  },
  {
    icon: Shield,
    title: 'Zusammenhalt',
    text: 'Förderung von Freundschaften und starkem Zusammenhalt. Jeder ist Teil der Gemeinschaft.',
    color: 'text-green-400',
  },
  {
    icon: Target,
    title: 'Einsatz & Teamgeist',
    text: 'Eishockey mit Freude, Einsatz und Teamgeist – das ist unser Weg.',
    color: 'text-yellow-400',
  },
];

export default function AboutSection() {
  const LOGO_URL = useClubLogo();
  return (
    <section id="verein" className="py-20 sm:py-24 lg:py-32" data-testid="about-section">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.6 }} className="mb-16">
          <span className="text-xs text-[#46C7FF] uppercase tracking-[0.2em] font-semibold">Über Uns</span>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-white mt-3 mb-6" style={{ fontFamily: 'Space Grotesk' }}>
            Mehr als nur ein Team
          </h2>
          <p className="text-white/60 max-w-3xl text-base sm:text-lg leading-relaxed">
            Die Dresden Devils sind ein traditionsreicher Amateur-Eishockeyverein aus Dresden mit echter Leidenschaft 
            für den Sport. Gegründet am 17. März 1997 aus dem Wunsch, Eishockey in Dresden selbstbestimmt, 
            mit Herz und Gemeinschaftsgeist zu gestalten.
          </p>
        </motion.div>

        {/* Main content grid */}
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12 lg:gap-16 mb-20">
          {/* Left: Timeline (3 cols) */}
          <div className="lg:col-span-3">
            <div className="relative">
              <div className="absolute left-[11px] top-2 bottom-2 w-px bg-gradient-to-b from-[#CC0000]/50 via-white/10 to-transparent" />
              <div className="space-y-6">
                {timeline.map((item, i) => (
                  <motion.div key={i} initial={{ opacity: 0, x: -20 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.15, duration: 0.5 }} className="relative pl-10">
                    <div className="absolute left-0 top-2 w-[23px] h-[23px] rounded-full bg-[hsl(0,0%,5%)] border-2 border-[#CC0000] flex items-center justify-center">
                      <div className="w-2.5 h-2.5 rounded-full bg-[#CC0000]" />
                    </div>
                    <div className="bg-[hsl(0,0%,8%)] border border-white/8 rounded-xl p-5 hover:border-white/15 transition-colors duration-200">
                      <div className="flex items-center gap-3 mb-2">
                        <span className="text-sm font-bold text-[#CC0000] font-mono-score tracking-wider">{item.year}</span>
                        <ChevronRight className="w-3 h-3 text-white/15" />
                        <span className="text-sm font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>{item.title}</span>
                      </div>
                      <p className="text-sm text-white/50 leading-relaxed">{item.text}</p>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>

          {/* Right: Info Cards (2 cols) */}
          <div className="lg:col-span-2 space-y-4">
            {/* Logo + Club name */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="bg-[hsl(0,0%,8%)] border border-[#CC0000]/15 rounded-xl p-6 text-center">
              <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden bg-[hsl(0,0%,12%)] p-1">
                <img src={LOGO_URL} alt="Devils Logo" className="w-full h-full object-contain rounded-full" style={{ background: '#141414' }} />
              </div>
              <div className="text-lg font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>EHV DRESDEN DEVILS</div>
              <div className="text-xs text-white/30 mt-1">e.V. &bull; Gegründet 1997</div>
            </motion.div>

            {/* Stats Grid */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.1 }} className="grid grid-cols-2 gap-3">
              {[
                { icon: Calendar, label: 'Gegründet', value: '1997', color: 'text-[#CC0000]' },
                { icon: Star, label: 'Sportart', value: 'Eishockey', color: 'text-[#46C7FF]' },
                { icon: Award, label: 'Liga', value: 'Regional', color: 'text-green-400' },
                { icon: Heart, label: 'Niveau', value: 'Amateur', color: 'text-yellow-400' },
              ].map((stat, i) => (
                <div key={i} className="bg-[hsl(0,0%,8%)] border border-white/8 rounded-xl p-4">
                  <stat.icon className={`w-4 h-4 ${stat.color} mb-2`} />
                  <div className="text-base font-bold text-white" style={{ fontFamily: 'Space Grotesk' }}>{stat.value}</div>
                  <div className="text-[10px] text-white/30 uppercase tracking-wider">{stat.label}</div>
                </div>
              ))}
            </motion.div>

            {/* Vorstand */}
            <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: 0.3 }} className="bg-[hsl(0,0%,8%)] border border-[#CC0000]/15 rounded-xl p-5">
              <h3 className="text-sm font-bold text-white mb-4 uppercase tracking-wider" style={{ fontFamily: 'Space Grotesk' }}>Vorstand</h3>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-[#CC0000]/10 flex items-center justify-center">
                    <span className="text-xs font-bold text-[#CC0000]">NB</span>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">Norman Bradler</div>
                    <div className="text-[10px] text-white/30">Präsident</div>
                  </div>
                </div>
                <div className="flex items-center gap-3">
                  <div className="w-8 h-8 rounded-full bg-white/5 flex items-center justify-center">
                    <span className="text-xs font-bold text-white/50">MR</span>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-white">Martin Rost</div>
                    <div className="text-[10px] text-white/30">Vizepräsident</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>

        {/* ── Vereinsphilosophie Section ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <div className="text-center mb-10">
            <span className="text-xs text-[#CC0000] uppercase tracking-[0.2em] font-semibold">Unsere Werte</span>
            <h3 className="text-2xl sm:text-3xl font-bold text-white mt-2" style={{ fontFamily: 'Space Grotesk' }}>
              Vereinsphilosophie
            </h3>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {philosophie.map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1, duration: 0.5 }}
                className="bg-[hsl(0,0%,8%)] border border-white/8 rounded-xl p-5 hover:border-white/15 transition-colors duration-200"
              >
                <item.icon className={`w-5 h-5 ${item.color} mb-3`} />
                <h4 className="text-sm font-bold text-white mb-2" style={{ fontFamily: 'Space Grotesk' }}>
                  {item.title}
                </h4>
                <p className="text-xs text-white/40 leading-relaxed">{item.text}</p>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* ── Mission Statement ── */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="bg-[hsl(0,0%,7%)] border border-[#CC0000]/10 rounded-2xl p-8 sm:p-10 text-center relative overflow-hidden"
        >
          {/* Subtle background */}
          <div className="absolute inset-0" style={{ background: 'radial-gradient(600px 300px at 50% 50%, rgba(204,0,0,0.04) 0%, transparent 70%)' }} />
          
          <div className="relative">
            <div className="w-16 h-16 mx-auto mb-6 rounded-full overflow-hidden bg-[hsl(0,0%,12%)] p-1">
              <img src={LOGO_URL} alt="Logo" className="w-full h-full object-contain rounded-full" style={{ background: '#141414' }} />
            </div>

            <div className="text-[#CC0000] text-3xl mb-2">&ldquo;</div>
            <p className="text-lg sm:text-xl text-white/70 italic leading-relaxed max-w-2xl mx-auto mb-2">
              Leidenschaft auf dem Eis, Gemeinschaft im Verein!
            </p>
            <div className="text-[#CC0000] text-3xl">&rdquo;</div>

            <div className="w-16 h-[2px] bg-[#CC0000]/30 mx-auto my-6" />

            <p className="text-sm text-white/40 leading-relaxed max-w-xl mx-auto">
              Unser Verein bietet Raum für Wettkampf, persönliche Entwicklung und Freizeitaktivität im Eishockey-Sport. 
              Jeder Spieler, Ehrenamtliche und Unterstützer ist Teil einer Gemeinschaft, 
              die Eishockey mit Freude und Teamgeist lebt.
            </p>

            <div className="flex flex-wrap items-center justify-center gap-3 mt-6">
              {['Landesliga Sachsen', 'Sachsenliga'].map((liga, i) => (
                <span key={i} className="text-[10px] text-white/25 border border-white/8 px-3 py-1 rounded-full tracking-wider uppercase">
                  {liga}
                </span>
              ))}
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
}
